import { memo, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Compass, type LucideIcon } from "lucide-react";
import { Building2, Package, ShoppingCart, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useMenuAccess } from "@/hooks/shared/use-menu-access";

interface QuickLink {
  label: string;
  path: string;
  icon: LucideIcon;
}

const QUICK_LINKS: QuickLink[] = [
  { label: "Clientes", path: "/dashboard/clients", icon: Users },
  { label: "Encomendas", path: "/dashboard/orders", icon: ShoppingCart },
  { label: "Fornecedores", path: "/dashboard/suppliers", icon: Building2 },
  { label: "Artigos", path: "/dashboard/articles", icon: Package },
];

/**
 * O 404.
 *
 * Mesma língua do ecrã de acesso negado: ícone leve, eyebrow, título leve,
 * dois caminhos de saída. Não é um erro grave — é uma morada errada.
 *
 * Os atalhos são filtrados pelas permissões de quem está a ver. Antes ofereciam
 * "Clientes" e "Fornecedores" a toda a gente, e quem lá fosse batia num "acesso
 * negado" — mandar alguém para uma porta fechada é pior do que não sugerir nada.
 */
export const NotFoundPage = memo(() => {
  const navigate = useNavigate();
  const { hasMenuAccess } = useMenuAccess();

  const links = useMemo(
    () => QUICK_LINKS.filter((l) => hasMenuAccess(l.path)),
    [hasMenuAccess]
  );

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-6 py-16">
      <div className="w-full max-w-md text-center">
        <Compass
          className="mx-auto h-6 w-6 text-muted-foreground/40"
          strokeWidth={1.5}
          aria-hidden
        />

        <p className="mt-6 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Erro 404
        </p>

        <h1 className="mt-3 text-3xl font-light tracking-tight text-foreground">
          Página não encontrada
        </h1>

        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          O endereço não existe ou a página foi removida.
        </p>

        <div className="mt-8 flex flex-col items-center justify-center gap-2 sm:flex-row">
          <Button asChild className="group h-10 w-full gap-2 rounded-lg sm:w-auto">
            <Link to="/dashboard">
              Ir para o painel
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </Button>

          <Button
            variant="ghost"
            className="group h-10 w-full gap-2 rounded-lg text-muted-foreground hover:text-foreground sm:w-auto"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            Voltar atrás
          </Button>
        </div>

        {links.length > 0 && (
          <div className="mt-10 border-t border-border/60 pt-6">
            <p className="mb-3 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Acesso rápido
            </p>
            <div className="flex flex-wrap justify-center gap-1.5">
              {links.map(({ path, icon: Icon, label }) => (
                <Link
                  key={path}
                  to={path}
                  className="flex items-center gap-2 rounded-lg border border-border/60 px-3 py-1.5 text-[13px] text-muted-foreground transition-colors hover:border-border hover:text-foreground"
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                  {label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

NotFoundPage.displayName = "NotFoundPage";
